import { useState, useEffect, useCallback } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { getPartnerIncoming, respondToPartnerInvite } from "../../services/api";
import { OrgAvatar, Button } from "./v2";
import PartnerConfirmDialog from "./PartnerConfirmDialog";

// Incoming partnership invites — the receiver's private consent inbox,
// rebuilt 1:1 from ARTBOARD-SPEC board 4c ("Incoming partnership invite"):
// white card, org avatar 44 r12, bold inviter name in the 14px line,
// consequence sub-line, Accept (primary) / Decline (secondary) + "View
// profile" link right-aligned. Accept → appears on both walls; Decline →
// gone. Never shown on the public wall. Admin-only; self-fetching;
// `reloadKey` bumps to refresh.
//
// PRESENTATION ONLY (Brief Rules 6/8): load/act/busy logic is
// byte-identical to the previous version. The "View profile" link wires
// the EXISTING page navigation handler (useAgencyProfile.handlePartnerClick
// → /search/agency/{id}) passed in as `onViewProfile` — no new logic.

const IncomingPartnerInvitesPanel = ({ reloadKey = 0, onChanged, onViewProfile }) => {
  const [invites, setInvites] = useState([]);
  const [busyId, setBusyId] = useState(null);
  // DECISION-037 — Decline is irreversible from this seat (the invite is
  // answered and the card disappears optimistically, so there is no undo and no
  // way to reach it again), while Accept and every partner action on the wall
  // already confirm. Holds the invite awaiting confirmation; null = no dialog.
  // Accept stays a single click by design: it is the reversible direction
  // (either side can unlink at any time, board 4c).
  const [declineTarget, setDeclineTarget] = useState(null);

  const load = useCallback(async () => {
    const res = await getPartnerIncoming();
    if (res.success) setInvites(Array.isArray(res.data) ? res.data : []);
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const act = async (inv, action) => {
    setBusyId(inv.partner_id);
    try {
      const res = await respondToPartnerInvite(inv.partner_id, action);
      if (res.success) {
        toast.success(action === "accept" ? "Partnership accepted." : "Invite declined.");
        setInvites((list) => list.filter((i) => i.partner_id !== inv.partner_id));
        onChanged?.();
      } else {
        toast.error(typeof res.error === "string" ? res.error : `Failed to ${action}`);
      }
    } finally {
      setBusyId(null);
    }
  };

  // The dialog's confirm: run the UNCHANGED action, then close. `act` swallows
  // its own errors, so the dialog always closes exactly once.
  const confirmDecline = async () => {
    if (!declineTarget) return;
    await act(declineTarget, "decline");
    setDeclineTarget(null);
  };

  if (!invites.length) return null;

  return (
    <div className="px-6 pt-4">
      <div className="text-[11px] font-semibold tracking-wide uppercase text-[#94A3B8] mb-2">
        Incoming partnership invites
      </div>
      <div className="flex flex-col gap-2">
        {invites.map((inv) => (
          <div
            key={inv.partner_id}
            className="bg-white border border-[#E3E9F1] rounded-xl shadow-[0_1px_2px_rgba(15,23,42,.05)] p-[18px] flex items-start gap-[13px]"
          >
            <OrgAvatar src={inv.logo_url} name={inv.name} seed={inv.partner_id ?? inv.name} size={44} radius={12} />
            <div className="flex-1 min-w-0">
              <div className="text-[14px] text-[#0F172A] leading-[1.45]">
                <b>{inv.name}</b> invited you to be partners on TransferPitch.
              </div>
              <div className="text-[12.5px] text-[#64748B] leading-[1.5] mt-1">
                Your logos appear on each other’s public walls. Either side can unlink at any time.
                {inv.sent_at ? ` · ${moment(inv.sent_at).fromNow()}` : ""}
              </div>
              <div className="flex items-center gap-2.5 mt-3.5">
                <Button
                  onClick={() => act(inv, "accept")}
                  disabled={busyId === inv.partner_id}
                  style={{ padding: "9px 18px" }}
                >
                  Accept
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setDeclineTarget(inv)}
                  disabled={busyId === inv.partner_id}
                  style={{ padding: "9px 18px" }}
                >
                  Decline
                </Button>
                {onViewProfile && (
                  <button
                    type="button"
                    onClick={() => onViewProfile(inv.partner_id)}
                    className="ml-auto self-center border-0 bg-transparent cursor-pointer text-[13px] font-medium text-[#1668D9] hover:text-[#0F56BC]"
                  >
                    View profile
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* DECISION-037 — the existing 4c confirm; the "declineInvite" copy
          follows the board's shape (consequence, then the way back) because no
          board draws a decline dialog. Close blocked while in flight. */}
      <PartnerConfirmDialog
        action={
          declineTarget
            ? { kind: "declineInvite", partner: { name: declineTarget.name } }
            : null
        }
        loading={busyId === declineTarget?.partner_id}
        onCancel={() =>
          busyId === declineTarget?.partner_id ? null : setDeclineTarget(null)
        }
        onConfirm={confirmDecline}
      />
    </div>
  );
};

export default IncomingPartnerInvitesPanel;
